import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Users as UsersIcon, Eye, RotateCcw } from 'lucide-react';
import './CommonStyles.css';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import './UserViewModal.css';
import './AllSellStyles.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import RolesList from './RolesList';
import UsersList from './UsersList';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const Users = ({ activeTab: propActiveTab }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, addToast, removeToast, showSuccess, showError, showWarning } = useToast();
  const { hasPermission, userRole, permissions, isLoading: permissionLoading } = usePermissionContext();

  // All hooks must be called before any conditional statements
  const [activeTab, setActiveTab] = useState(propActiveTab || location.state?.activeTab || 'users');

  useEffect(() => {
    if (propActiveTab) {
      setActiveTab(propActiveTab);
    }
  }, [propActiveTab]);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Check if user has access to current active tab, if not switch to first available tab
  useEffect(() => {
    if (!permissionLoading) {
      console.log('🔐 RBAC DEBUG - Checking access to active tab:', activeTab);

      if (activeTab === 'users' && !hasPermission('user.view')) {
        console.log('🔐 RBAC DEBUG - User does not have user.view permission, switching tab');
        if (hasPermission('role.view')) {
          setActiveTab('roles');
        } else if (hasPermission('sales_commission_agent.view')) {
          setActiveTab('agents');
        }
      } else if (activeTab === 'roles' && !hasPermission('role.view')) {
        console.log('🔐 RBAC DEBUG - User does not have role.view permission, switching tab');
        if (hasPermission('user.view')) {
          setActiveTab('users');
        } else if (hasPermission('sales_commission_agent.view')) {
          setActiveTab('agents');
        }
      } else if (activeTab === 'agents' && !hasPermission('sales_commission_agent.view')) {
        console.log('🔐 RBAC DEBUG - User does not have sales_commission_agent.view permission, switching tab');
        if (hasPermission('user.view')) {
          setActiveTab('users');
        } else if (hasPermission('role.view')) {
          setActiveTab('roles');
        }
      }
    }
  }, [permissionLoading, activeTab, hasPermission]);

  // Load data when active tab changes
  useEffect(() => {
    if (!permissionLoading) {
      loadData(activeTab);
    }
  }, [activeTab, permissionLoading]);

  // Listen for navigation state changes to detect when returning from user form
  useEffect(() => {
    if (location.state?.userCreated) {
      showSuccess('User created successfully!');
      // Clear the state to prevent showing message again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.userUpdated) {
      showSuccess('User updated successfully!');
      // Clear the state to prevent showing message again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, showSuccess]);

  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = data.filter(item => {
      if (activeTab === 'users') {
        const matchesSearch =
          (item.username || '').toLowerCase().includes(lowerSearch) ||
          (item.full_name || `${item.first_name || ''} ${item.last_name || ''}`).toLowerCase().includes(lowerSearch) ||
          (item.email || '').toLowerCase().includes(lowerSearch);

        const roleName = (item.role?.name || item.role_name || item.role || '').toLowerCase();
        const matchesRole = filterRole === 'all' || roleName === filterRole.toLowerCase();

        let isActive = item.status === 'active' || item.is_active === true || item.is_active === 1;
        const matchesStatus =
          filterStatus === 'all' ||
          (filterStatus === 'active' && isActive) ||
          (filterStatus === 'inactive' && !isActive);

        return matchesSearch && matchesRole && matchesStatus;
      } else if (activeTab === 'roles') {
        return (item.name || '').toLowerCase().includes(lowerSearch);
      } else if (activeTab === 'agents') {
        return (item.name || '').toLowerCase().includes(lowerSearch) ||
          (item.email || '').toLowerCase().includes(lowerSearch);
      }
      return false;
    });
    setFilteredData(filtered);
    setCurrentPage(1); // reset to first page on filter change
  }, [searchTerm, filterRole, filterStatus, data, activeTab]);

  // Show loading state while permissions are loading
  if (permissionLoading) {
    console.log('🔐 RBAC DEBUG - Still loading permissions, showing loading state');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading permissions...</div>
        <div style={{ fontSize: '14px', color: '#999' }}>Please wait while we set up your access rights</div>
      </div>
    );
  }

  const handleExport = (format) => {
    if (!filteredData || filteredData.length === 0) {
      showWarning('No data to export');
      setShowExportDropdown(false);
      return;
    }

    let headers = [];
    let rows = [];
    let title = '';
    let filename = '';

    if (activeTab === 'users') {
      headers = ['Username', 'Full Name', 'Role', 'Email', 'Status'];
      rows = filteredData.map(item => [
        item.username || '',
        item.full_name || `${item.first_name || ''} ${item.last_name || ''}`,
        item.role?.name || item.role_name || item.role || '',
        item.email || '',
        (item.status === 'active' || item.is_active) ? 'Active' : 'Inactive'
      ]);
      title = 'Users List';
      filename = 'users';
    } else if (activeTab === 'roles') {
      headers = ['Role Name', 'Permissions Count', 'Users Count', 'Type'];
      rows = filteredData.map(item => [
        item.name || '',
        item.permissions?.length || 0,
        item.users_count || 0,
        item.is_default ? 'Default' : 'Custom'
      ]);
      title = 'Roles List';
      filename = 'roles';
    } else if (activeTab === 'agents') {
      headers = ['Name', 'Email', 'Contact Number', 'Address', 'Commission %'];
      rows = filteredData.map(item => [
        item.name || '',
        item.email || '',
        item.contact_no || '',
        item.address || '',
        `${item.cmmsn_percent || 0}%`
      ]);
      title = 'Sales Commission Agents';
      filename = 'agents';
    }

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { showWarning('Column visibility settings'); setShowExportDropdown(false); };


  // Pagination state is now declared at the top of the component

  const loadData = async (tab) => {
    setIsLoading(true);
    try {
      let result = [];
      if (tab === 'users') {
        const userDetails = await apiService.getUserDetails();
        const businessId = userDetails?.business_id || 40;

        const res = await apiService.getUsers({
          business_id: businessId,
          per_page: -1 // Get all users
        });
        result = res.data || res || [];
      } else if (tab === 'roles') {
        // Roles are handled by RolesList component
        result = [];
      } else if (tab === 'agents') {
        const res = await apiService.getSalesCommissionAgents();
        result = res.data || res || [];
      }
      setData(result);
      setFilteredData(result);
    } catch (error) {
      console.error(`Error loading ${tab}:`, error);
      setData([]);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderRecordDetails = () => {
    if (!selectedRecord) return null;

    if (activeTab === 'users') {
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Main info row */}
          <div className="customer-service-section">
            <div className="customer-info-box">
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem' }}>Personal Details</h3>
              <div className="info-item">
                <strong>Full Name</strong>
                <div>{selectedRecord.prefix ? `${selectedRecord.prefix}. ` : ''}{selectedRecord.full_name || `${selectedRecord.first_name || ''} ${selectedRecord.last_name || ''}`}</div>
              </div>
              <div className="info-item" style={{ marginTop: '0.5rem' }}>
                <strong>Username</strong>
                <div>{selectedRecord.username}</div>
              </div>
              <div className="info-item" style={{ marginTop: '0.5rem' }}>
                <strong>Email Address</strong>
                <div>{selectedRecord.email || '--'}</div>
              </div>
              <div className="info-item" style={{ marginTop: '0.5rem' }}>
                <strong>Mobile Number</strong>
                <div>{selectedRecord.contact_number || selectedRecord.mobile || '--'}</div>
              </div>
            </div>
            <div className="service-info-box">
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem' }}>Account Status</h3>
              <div className="info-item">
                <strong>Status</strong>
                <div>
                  <span style={{ 
                    padding: '0.2rem 0.6rem', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem', 
                    fontWeight: '700',
                    backgroundColor: (selectedRecord.status === 'active' || selectedRecord.is_active) ? '#dcfce7' : '#fee2e2',
                    color: (selectedRecord.status === 'active' || selectedRecord.is_active) ? '#166534' : '#991b1b'
                  }}>
                    {(selectedRecord.status === 'active' || selectedRecord.is_active) ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="info-item" style={{ marginTop: '0.5rem' }}>
                <strong>Commission %</strong>
                <div>{selectedRecord.cmmsn_percent || 0}%</div>
              </div>
              <div className="info-item" style={{ marginTop: '0.5rem' }}>
                <strong>Max Discount %</strong>
                <div>{selectedRecord.max_sales_discount_percent || 0}%</div>
              </div>
            </div>
          </div>

          {/* More details */}
          <div className="invoice-section">
            <h3>Additional Information</h3>
            <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              <div className="info-item">
                <strong>Date of Birth</strong>
                <div>{selectedRecord.dob || selectedRecord.date_of_birth || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Gender</strong>
                <div>{selectedRecord.gender || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Marital Status</strong>
                <div>{selectedRecord.marital_status || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Blood Group</strong>
                <div>{selectedRecord.blood_group || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Guardian Name</strong>
                <div>{selectedRecord.guardian_name || '--'}</div>
              </div>
              <div className="info-item">
                <strong>ID Proof</strong>
                <div>{selectedRecord.id_proof_name || '--'} {selectedRecord.id_proof_number ? `(${selectedRecord.id_proof_number})` : ''}</div>
              </div>
            </div>
          </div>

          <div className="invoice-section">
            <h3>Addresses</h3>
            <div className="customer-service-section">
              <div className="customer-info-box">
                <div className="info-item">
                  <strong>Current Address</strong>
                  <div>{selectedRecord.current_address || 'Not specified'}</div>
                </div>
              </div>
              <div className="service-info-box">
                <div className="info-item">
                  <strong>Permanent Address</strong>
                  <div>{selectedRecord.permanent_address || 'Not specified'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="invoice-section">
            <h3>Bank & HRM Details</h3>
            <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              <div className="info-item">
                <strong>Account Name</strong>
                <div>{selectedRecord.bank_details?.account_holder_name || selectedRecord.account_holder_name || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Bank Name</strong>
                <div>{selectedRecord.bank_details?.bank_name || selectedRecord.bank_name || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Account Number</strong>
                <div>{selectedRecord.bank_details?.account_number || selectedRecord.account_number || '--'}</div>
              </div>
              <div className="info-item">
                <strong>Department</strong>
                <div>
                  {(() => {
                    const deptMap = { '1': 'Operations', '2': 'Sales', '3': 'Support' };
                    const deptId = selectedRecord.department_id || selectedRecord.department;
                    return selectedRecord.department_name || (typeof deptId === 'object' ? deptId?.name : deptMap[deptId] || deptId) || '--';
                  })()}
                </div>
              </div>
              <div className="info-item">
                <strong>Designation</strong>
                <div>
                  {(() => {
                    const desigMap = { '1': 'Manager', '2': 'Staff', '3': 'Admin' };
                    const desigId = selectedRecord.designation_id || selectedRecord.designation;
                    return selectedRecord.designation_name || (typeof desigId === 'object' ? desigId?.name : desigMap[desigId] || desigId) || '--';
                  })()}
                </div>
              </div>
              <div className="info-item">
                <strong>Basic Salary</strong>
                <div>{selectedRecord.basic_salary ? `$${selectedRecord.basic_salary}` : '--'} ({selectedRecord.pay_period || 'per_month'})</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'roles') {
      return (
        <div className="customer-service-section" style={{ gridTemplateColumns: '1fr', width: '100%' }}>
          <div className="customer-info-box">
            <div className="info-item">
              <strong>Role Name</strong>
              <div>{selectedRecord.name || 'N/A'}</div>
            </div>
            <div className="info-item" style={{ marginTop: '0.75rem' }}>
              <strong>Description</strong>
              <div>{selectedRecord.description || 'N/A'}</div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'agents') {
      return (
        <div className="customer-service-section" style={{ width: '100%' }}>
          <div className="customer-info-box">
            <div className="info-item">
              <strong>Name</strong>
              <div>{selectedRecord.name || 'N/A'}</div>
            </div>
            <div className="info-item" style={{ marginTop: '0.75rem' }}>
              <strong>Email</strong>
              <div>{selectedRecord.email || 'N/A'}</div>
            </div>
            <div className="info-item" style={{ marginTop: '0.75rem' }}>
              <strong>Contact Number</strong>
              <div>{selectedRecord.contact_no || 'N/A'}</div>
            </div>
          </div>
          <div className="service-info-box">
            <div className="info-item">
              <strong>Address</strong>
              <div>{selectedRecord.address || 'N/A'}</div>
            </div>
            <div className="info-item" style={{ marginTop: '0.75rem' }}>
              <strong>Commission %</strong>
              <div>{selectedRecord.cmmsn_percent || 0}%</div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Calculate Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const toggleActionMenu = (id) => {
    setOpenActionId(openActionId === id ? null : id);
  };

  const handleViewRecord = (record) => {
    if (!record) return;
    
    // Set initial record from list for immediate feedback
    setSelectedRecord(record);
    setShowViewModal(true);
    setOpenActionId(null);
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
  };

  const handleDeleteAgent = (agent) => {
    if (window.confirm(`Are you sure you want to delete sales commission agent "${agent.name}"?`)) {
      setData(prev => prev.filter(a => a.id !== agent.id));
      setFilteredData(prev => prev.filter(a => a.id !== agent.id));
      showSuccess('Sales commission agent deleted successfully!');
    }
  };

  const handleAdd = () => {
    console.log('🔐 RBAC DEBUG - handleAdd called for tab:', activeTab);

    if (activeTab === 'users') {
      console.log('🔐 RBAC DEBUG - Checking user.create permission');
      // Check if user has permission to create users
      if (!hasPermission('user.create')) {
        console.log('🔐 RBAC DEBUG - user.create permission denied');
        showWarning('You do not have permission to create users');
        return;
      }
      console.log('🔐 RBAC DEBUG - user.create permission granted, navigating to add user');
      navigate('/users/add');
    } else if (activeTab === 'roles') {
      console.log('🔐 RBAC DEBUG - Checking role.create permission');
      // Check if user has permission to create roles
      if (!hasPermission('role.create')) {
        console.log('🔐 RBAC DEBUG - role.create permission denied');
        showWarning('You do not have permission to create roles');
        return;
      }
      console.log('🔐 RBAC DEBUG - role.create permission granted, navigating to add role');
      navigate('/roles/add');
    } else if (activeTab === 'agents') {
      console.log('🔐 RBAC DEBUG - Checking sales_commission_agent.create permission');
      // Check if user has permission to create sales commission agents
      if (!hasPermission('sales_commission_agent.create')) {
        console.log('🔐 RBAC DEBUG - sales_commission_agent.create permission denied');
        showWarning('You do not have permission to create sales commission agents');
        return;
      }
      console.log('🔐 RBAC DEBUG - sales_commission_agent.create permission granted, navigating to add agent');
      navigate('/sales-commission-agents/add');
    }
  };

  const renderTable = () => {
    // Show loading state
    if (isLoading && activeTab !== 'users') {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          minHeight: '300px'
        }}>
          <div style={{
            fontSize: '24px',
            marginBottom: '12px',
            color: '#64748b'
          }}>
            ⏳ Loading {activeTab === 'roles' ? 'Roles' : 'Sales Commission Agents'}...
          </div>
          <div style={{
            fontSize: '14px',
            color: '#94a3b8'
          }}>
            Fetching {activeTab === 'roles' ? 'roles' : 'agents'} data from server...
          </div>
        </div>
      );
    }

    if (activeTab === 'users') {
      return <UsersList onView={handleViewRecord} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={itemsPerPage} />;
    } else if (activeTab === 'roles') {
      return <RolesList onView={handleViewRecord} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={itemsPerPage} />;
    } else if (activeTab === 'agents') {
      if (filteredData.length === 0) {
        return (
          <div className="no-sales">
            <h3>📊 No Sales Commission Agent Found</h3>
            <p>No sales commission agents have been recorded yet.</p>
          </div>
        );
      }
      return (
        <table className="products-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Name</th>
              <th>Email</th>
              <th>Contact Number</th>
              <th>Address</th>
              <th>Commission %</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map(agent => (
              <tr key={agent.id}>
                <td className="actions-cell">
                  <div className="action-dropdown-container">
                    <button
                      className="action-btn-trigger"
                      onClick={() => toggleActionMenu(agent.id)}
                    >
                      Actions <span className="arrow-down">▼</span>
                    </button>
                    {openActionId === agent.id && (
                      <div className="action-dropdown-menu">
                        <button onClick={() => handleViewRecord(agent)}>View</button>
                        <button onClick={() => navigate('/sales-commission-agents/add', { state: { agent } })}>Edit</button>
                        <button onClick={() => handleDeleteAgent(agent)}>Delete</button>
                      </div>
                    )}
                  </div>
                </td>
                <td>{agent.name}</td>
                <td>{agent.email}</td>
                <td>{agent.contact_no}</td>
                <td>{agent.address}</td>
                <td>{agent.cmmsn_percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  };

  return (
    <div className="products-container">
      {/* Main Card: Tabs + Table Controls + Table */}
      <div className="main-card">


        {/* Table Controls Row */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(true)}
                title="Open Filters"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            {(() => {
              console.log('🔐 RBAC DEBUG - Checking Add button visibility for tab:', activeTab);
              const canCreateUsers = activeTab === 'users' && (permissions.length === 0 || hasPermission('user.create'));
              const canCreateRoles = activeTab === 'roles' && (permissions.length === 0 || hasPermission('role.create'));
              const canCreateAgents = activeTab === 'agents' && (permissions.length === 0 || hasPermission('sales_commission_agent.create'));

              console.log('🔐 RBAC DEBUG - Can create users:', canCreateUsers);
              console.log('🔐 RBAC DEBUG - Can create roles:', canCreateRoles);
              console.log('🔐 RBAC DEBUG - Can create agents:', canCreateAgents);
              console.log('🔐 RBAC DEBUG - Show Add button:', canCreateUsers || canCreateRoles || canCreateAgents);

              return canCreateUsers || canCreateRoles || canCreateAgents;
            })() && (
                <button className="control-btn-primary" onClick={handleAdd}>
                  <span className="plus-icon">+</span> Add
                </button>
              )}
          </div>
        </div>

        {/* Data Table */}
        <div className="products-table-container">
          {renderTable()}
        </div>

        {/* Pagination Controls */}
        {activeTab === 'agents' && filteredData.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ color: '#334155', fontSize: '0.9rem', fontWeight: '500' }}>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', color: '#334155', fontWeight: '500', fontSize: '0.95rem' }}>
                Page {currentPage} of {totalPages || 1}
              </span>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {showViewModal && (
        <div className="modal-overlay">
          <div className="modal-container invoice-modal">
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <h2>View {activeTab === 'users' ? 'User' : activeTab === 'roles' ? 'Role' : 'Sales Commission Agent'}</h2>
                {activeTab === 'users' && data.length > 0 && (
                  <div style={{ position: 'relative' }}>
                    <select 
                      value={selectedRecord?.id}
                      onChange={(e) => {
                        const user = data.find(u => u.id === parseInt(e.target.value));
                        if (user) handleViewRecord(user);
                      }}
                      style={{
                        padding: '0.4rem 2rem 0.4rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: 'white',
                        fontSize: '0.9rem',
                        color: '#64748b',
                        cursor: 'pointer',
                        minWidth: '200px'
                      }}
                    >
                      {data.filter(u => u.username).map(u => (
                        <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
            </div>
            <div className="modal-content invoice-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {renderRecordDetails()}
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                {activeTab === 'users' && (
                  <>
                    <div className="filter-group">
                      <label>Role:</label>
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                      >
                        <option value="all">All Roles</option>
                        {/* Populate from distinct roles in data */}
                        {[...new Set(data.map(u => u.role?.name || u.role_name || u.role).filter(Boolean))].sort().map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>Status:</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </>
                )}
                {activeTab === 'roles' && (
                  <div className="filter-group">
                    <label>Search Role:</label>
                    <input
                      type="text"
                      placeholder="Role Name"
                      className="form-input"
                      style={{ padding: '0.6rem' }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                )}
                {activeTab === 'agents' && (
                  <div className="filter-group">
                    <label>Contact Number:</label>
                    <input type="text" placeholder="Contact" className="form-input" style={{ padding: '0.6rem' }} />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn"
                onClick={() => { setFilterRole('all'); setFilterStatus('all'); setSearchTerm(''); }}
                style={{ 
                  marginRight: 'auto',
                  background: 'var(--etpos-card, #fff)',
                  color: '#64748b',
                  border: '1.5px solid var(--etpos-border, #e2e8f0)',
                  fontSize: '0.9rem',
                  padding: '0.55rem 1.1rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  whiteSpace: 'nowrap',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--etpos-bg, #f8fafc)';
                  e.currentTarget.style.borderColor = '#94a3b8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--etpos-card, #fff)';
                  e.currentTarget.style.borderColor = 'var(--etpos-border, #e2e8f0)';
                }}
              >
                <RotateCcw size={15} />
                Reset Filters
              </button>
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default Users;
